import { atom, getDefaultStore, useAtomValue } from 'jotai'
import { useEffect, useReducer } from 'react'
import { parse } from '@nostr/tools/nip27'
import { noteEncode, naddrEncode, npubEncode } from '@nostr/tools/nip19'
import { Event, NostrEvent } from '@nostr/tools/wasm'
import { getActiveResource } from '@/services/translations.service'
import { createPromptSession, type PromptSession } from '@/lib/prompt-api'

export interface TranslationEntry {
  content: string
  showOriginal: boolean
  inflight: boolean
}

const cache = new Map<string, ReturnType<typeof atom<TranslationEntry | undefined>>>()

export const emptyTranslationAtom = atom<TranslationEntry | undefined>(undefined)

export function getTranslationAtomIfExists(eventId: string) {
  return cache.get(eventId)
}

const listeners = new Map<string, Set<() => void>>()

function notifyTranslationCreated(eventId: string) {
  listeners.get(eventId)?.forEach((cb) => cb())
}

export function useNoteTranslation(event?: NostrEvent) {
  const eventId = event?.id
  const [, forceRender] = useReducer((x: number) => x + 1, 0)
  const atom_ = eventId ? getTranslationAtomIfExists(eventId) : null

  useEffect(() => {
    if (!eventId) return
    if (getTranslationAtomIfExists(eventId)) return

    const cb = () => forceRender()
    let set = listeners.get(eventId)
    if (!set) {
      set = new Set()
      listeners.set(eventId, set)
    }
    set.add(cb)
    return () => {
      set!.delete(cb)
      if (set!.size === 0) listeners.delete(eventId)
    }
  }, [eventId])

  return useAtomValue(atom_ ?? emptyTranslationAtom)
}

function getOrCreateTranslationAtom(eventId: string) {
  let atom_ = cache.get(eventId)
  if (!atom_) {
    atom_ = atom<TranslationEntry | undefined>(undefined)
    cache.set(eventId, atom_)
    notifyTranslationCreated(eventId)
  }
  return atom_
}

const store = getDefaultStore()

export function saveTranslation(eventId: string, content: string) {
  store.set(getOrCreateTranslationAtom(eventId), { content, showOriginal: false, inflight: false })
}

export function setTranslationInflight(eventId: string, inflight: boolean) {
  const atom_ = getOrCreateTranslationAtom(eventId)
  const prev = store.get(atom_)
  store.set(atom_, {
    content: prev?.content ?? '',
    showOriginal: prev?.showOriginal ?? false,
    inflight
  })
}

export function toggleShowOriginal(eventId: string) {
  const atom_ = getTranslationAtomIfExists(eventId)
  if (!atom_) return
  const prev = store.get(atom_)
  if (!prev) return
  store.set(atom_, { ...prev, showOriginal: !prev.showOriginal })
}

function buildPlaceholderMap(content: string) {
  const urls: string[] = []
  const refs: string[] = []
  const masked: string[] = []
  let urlIdx = 0
  let refIdx = 0

  for (const block of parse(content)) {
    if (block.type === 'reference') {
      const p = block.pointer as {
        id?: string
        pubkey?: string
        kind?: number
        identifier?: string
      }
      const encoded = p.id
        ? noteEncode(p.id)
        : p.identifier && p.pubkey && p.kind
          ? naddrEncode({ kind: p.kind, pubkey: p.pubkey, identifier: p.identifier })
          : p.pubkey
            ? npubEncode(p.pubkey)
            : ''
      if (encoded) {
        refs.push(encoded)
        masked.push(`[[REF_${refIdx++}]]`)
      }
    } else if (
      block.type === 'url' ||
      block.type === 'image' ||
      block.type === 'video' ||
      block.type === 'audio' ||
      block.type === 'relay'
    ) {
      urls.push(block.url)
      masked.push(`[[URL_${urlIdx++}]]`)
    } else if (block.type === 'text') {
      masked.push(block.text)
    } else if (block.type === 'emoji') {
      masked.push(`:${block.shortcode}:`)
    } else if (block.type === 'hashtag') {
      masked.push(`#${block.value}`)
    }
  }

  return { masked: masked.join(''), urls, refs }
}

function restorePlaceholders(text: string, urls: string[], refs: string[]) {
  let urlIdx = 0
  let refIdx = 0
  return text
    .replace(/\[\[URL_\d+\]\]/g, () => urls[urlIdx++] ?? '[URL]')
    .replace(/\[\[REF_\d+\]\]/g, () => refs[refIdx++] ?? '[REF]')
}

let translationTarget: string | null = null

async function detectTranslationTarget(session: PromptSession) {
  if (translationTarget) return

  const r = getActiveResource()
  const values = Object.entries(r ?? {})
    .filter(([k]) => !k.startsWith('_'))
    .map(([, v]) => v)
  if (values.length === 0) {
    translationTarget = 'english'
    return
  }

  const pool = [...values]
  const sample: string[] = []
  while (sample.length < 40 && pool.length > 0) {
    sample.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0])
  }

  const language = await session.prompt(
    `Identify the language of the following text. Reply with just the language name, e.g. "english", "french", "chinese".\n\n${sample.join('\n')}`
  )
  translationTarget = language?.trim() || 'english'
}

export async function translateAndSave(event: Event) {
  setTranslationInflight(event.id, true)
  let session: PromptSession | undefined
  try {
    session = await createPromptSession(
      'Follow the user instruction for each translation task. Reply only with the requested output.'
    )
    if (!session) return

    await detectTranslationTarget(session)
    const { masked, urls, refs } = buildPlaceholderMap(event.content)
    const systemPrompt = `You are a translator. Translate the user's text into ${translationTarget}. Preserve all [[REF_N]] and [[URL_N]] placeholders exactly as they appear — do not translate, alter, expand, or move them. Keep line breaks. Output only the translated text (with the placeholders), no preambles.`

    const translated = await session.prompt(`${systemPrompt}\n\n${masked}`)
    saveTranslation(event.id, restorePlaceholders(translated ?? '', urls, refs))
  } finally {
    session?.destroy()
    setTranslationInflight(event.id, false)
  }
}
